import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import API_CONFIG, { getBaseUrl } from '../config/apiConfig';

function ImageGallery({ images, folder, isLocal = false }) {
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(null);
    const { t } = useTranslation();

    const closeFullscreen = () => {
        setSelectedImage(null);
        setCurrentIndex(null);
    };

    const handleImageClick = (image, index) => {
        setSelectedImage(image);
        setCurrentIndex(index);
    };

    const nextImage = () => {
        const nextIndex = (currentIndex + 1) % images.length;
        setSelectedImage(images[nextIndex]);
        setCurrentIndex(nextIndex);
    };

    const prevImage = () => {
        const prevIndex = (currentIndex - 1 + images.length) % images.length;
        setSelectedImage(images[prevIndex]);
        setCurrentIndex(prevIndex);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedImage) return;
            switch (e.key) {
                case 'Escape': closeFullscreen(); break;
                case 'ArrowRight': nextImage(); break;
                case 'ArrowLeft': prevImage(); break;
                default: break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, currentIndex, images]);

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="h-8 w-1 bg-[#4c9fb9] rounded-full" />
                <h2 className="text-xl font-black text-white tracking-tight">
                    {t('Bildergalerie (Vorschau)')}
                </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {images && images.length > 0 ? (
                    images.map((image, index) => {
                        const src = isLocal
                            ? image.url
                            : `${getBaseUrl()}${API_CONFIG.ENDPOINTS.PREVIEW_IMAGE}?folder=${folder}&image=${image.name}`;
                        return (
                            <div
                                key={index}
                                className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer border border-white/5 bg-white/5 hover:border-[#4c9fb9]/50 transition-all duration-300 shadow-lg hover:shadow-[#4c9fb9]/10"
                                onClick={() => handleImageClick(image, index)}
                            >
                                <img
                                    loading="lazy"
                                    src={src}
                                    alt={`Vorschau ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <Maximize2 className="text-white scale-75 group-hover:scale-100 transition-transform duration-300" size={24} />
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="col-span-full py-12 text-center rounded-3xl border-2 border-dashed border-white/10 bg-white/5">
                        <p className="text-slate-500 font-medium">{t('Keine Bilder vorhanden')}</p>
                    </div>
                )}
            </div>

            {/* Fullscreen Overlay */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center animate-in fade-in duration-300"
                    onClick={closeFullscreen}
                >
                    <button
                        className="absolute top-8 right-8 p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all"
                        onClick={closeFullscreen}
                    >
                        <X size={32} />
                    </button>

                    <div className="relative w-full h-full flex items-center justify-center p-4 sm:p-12" onClick={e => e.stopPropagation()}>
                        <button
                            className="absolute left-4 sm:left-12 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all hidden sm:block"
                            onClick={prevImage}
                        >
                            <ChevronLeft size={48} />
                        </button>

                        <img
                            loading="lazy"
                            src={
                                isLocal
                                    ? selectedImage.url
                                    : `${getBaseUrl()}${API_CONFIG.ENDPOINTS.PREVIEW_IMAGE}?folder=${folder}&image=${selectedImage.name}`
                            }
                            alt="Fullscreen"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
                        />

                        <button
                            className="absolute right-4 sm:right-12 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all hidden sm:block"
                            onClick={nextImage}
                        >
                            <ChevronRight size={48} />
                        </button>

                        {/* Mobile Navigation */}
                        <div className="absolute bottom-12 flex items-center gap-8 sm:hidden">
                            <button className="p-4 bg-white/10 rounded-full text-white" onClick={prevImage}>
                                <ChevronLeft size={32} />
                            </button>
                            <button className="p-4 bg-white/10 rounded-full text-white" onClick={nextImage}>
                                <ChevronRight size={32} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImageGallery;
